import { FC, ReactNode } from 'react';
import {
  ChevronRight,
  MoreHorizontal,
  Copy,
  Trash2,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import {
  WordPressSiteForm,
  type WordPressSiteFormState,
  type WordPressSiteFormErrors,
} from './wordpress-site-form';

interface WordPressSiteCardProps {
  site: WordPressSiteFormState;
  isOpen: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<WordPressSiteFormState>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  isOnlySite: boolean;
  errors: WordPressSiteFormErrors;
  hasErrors: boolean;
  disabled?: boolean;
  isEditing?: boolean;
  /**
   * When provided, the expanded card renders a per-site Save/Cancel footer and
   * calls this on save. Used by the inline editor on the collection page; the
   * modal omits it (it batches all sites into a single collection save).
   */
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isDirty?: boolean;
  /** Hide the duplicate/remove actions menu (inline editor edits existing sites only). */
  showActions?: boolean;
  /** Hide just the "Duplicate site" action (inline editor supports remove but not duplicate). */
  showDuplicate?: boolean;
  /** Extra content rendered in the collapsed summary line (e.g. last-indexed). */
  summaryExtra?: ReactNode;
  /** Extra content rendered inside the expanded body, above the save footer (e.g. content-filters button). */
  bodyExtra?: ReactNode;
}

const describeCron = (cron: string): string => {
  const trimmed = cron.trim();
  if (!trimmed) return 'No schedule';

  const parts = trimmed.split(/\s+/);
  if (parts.length !== 5) return trimmed;

  const [minute, hour, day, month, weekday] = parts;

  // Common patterns
  if (
    minute === '0' &&
    hour === '0' &&
    day === '*' &&
    month === '*' &&
    weekday === '*'
  ) {
    return 'Daily at midnight';
  }
  if (
    minute === '0' &&
    hour !== '*' &&
    day === '*' &&
    month === '*' &&
    weekday === '*'
  ) {
    const h = parseInt(hour, 10);
    if (!isNaN(h)) {
      const ampm = h >= 12 ? 'PM' : 'AM';
      const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return `Daily at ${h12}:00 ${ampm}`;
    }
  }
  if (day === '*' && month === '*' && weekday === '0') {
    return 'Weekly on Sunday';
  }
  if (day === '1' && month === '*' && weekday === '*') {
    return 'Monthly on the 1st';
  }

  return trimmed;
};

const describeAuthStatus = (site: WordPressSiteFormState): string => {
  if (site.hasCredentials) return 'Credentials stored';
  if (site.useAuth) return 'Auth enabled';
  return 'No auth';
};

export const WordPressSiteCard: FC<WordPressSiteCardProps> = ({
  site,
  isOpen,
  onToggle,
  onChange,
  onRemove,
  onDuplicate,
  isOnlySite,
  errors,
  hasErrors,
  disabled = false,
  isEditing = false,
  onSave,
  onCancel,
  isSaving = false,
  isDirty = false,
  showActions = true,
  showDuplicate = true,
  summaryExtra,
  bodyExtra,
}) => {
  return (
    <Collapsible open={isOpen} onOpenChange={onToggle}>
      <div className='rounded-md border border-border'>
        {/* Header row */}
        <div className='flex items-center justify-between w-full p-3'>
          <CollapsibleTrigger asChild>
            <button
              type='button'
              className='flex items-center gap-2 flex-1 min-w-0 text-left'
            >
              <ChevronRight
                className={cn(
                  'size-4 shrink-0 transition-transform duration-200',
                  isOpen && 'rotate-90',
                )}
              />
              <span className='font-medium text-sm truncate max-w-[180px]'>
                {site.label || 'Untitled site'}
              </span>
              <span className='text-xs text-muted-foreground truncate max-w-[200px]'>
                {site.baseUrl || 'No URL set'}
              </span>
              {hasErrors && (
                <span className='h-2 w-2 rounded-full bg-destructive shrink-0' />
              )}
            </button>
          </CollapsibleTrigger>
          {showActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-8 shrink-0'
                  onClick={(e) => e.stopPropagation()}
                  aria-label='Site options'
                >
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                {showDuplicate && (
                  <>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        onDuplicate();
                      }}
                    >
                      <Copy className='size-4' />
                      Duplicate site
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  variant='destructive'
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemove();
                  }}
                  disabled={isOnlySite}
                >
                  <Trash2 className='size-4' />
                  Remove site
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Summary line (only when collapsed) */}
        {!isOpen && (
          <div className='px-3 pb-2 pl-9 text-xs text-muted-foreground'>
            {describeCron(site.scheduleCron)} · {describeAuthStatus(site)} ·{' '}
            {site.cronEnabled ? 'idle' : 'paused'}
            {summaryExtra}
          </div>
        )}

        {/* Expanded form content */}
        <CollapsibleContent>
          <div className='p-4 pt-0 border-t border-border'>
            <div className='pt-4'>
              <WordPressSiteForm
                site={site}
                onChange={onChange}
                errors={errors}
                disabled={disabled}
                isEditing={isEditing}
              />
              {bodyExtra && <div className='mt-4'>{bodyExtra}</div>}
              {onSave && (
                <div className='mt-4 flex items-center justify-end gap-2 border-t border-border pt-4'>
                  {onCancel && (
                    <Button
                      type='button'
                      variant='ghost'
                      size='sm'
                      onClick={onCancel}
                      disabled={isSaving || !isDirty}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button
                    type='button'
                    size='sm'
                    onClick={onSave}
                    disabled={disabled || isSaving || hasErrors || !isDirty}
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className='size-4 mr-2 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      'Save site'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
};
